import React, { useState, useEffect } from 'react';
import Component from '../../components/Component';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  IconButton,
  Alert,
  Snackbar,
  Pagination,
  CircularProgress
} from '@mui/material';
import { Edit, Delete, Add, CloudUpload } from '@mui/icons-material';
import { createSticker, getStickers, updateSticker, deleteSticker } from '../../services/stickersApi';

function Stickers() {
  const [stickers, setStickers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSticker, setEditingSticker] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Form states
  const [formData, setFormData] = useState({
    title: '',
    type: 'image',
    category: '',
    tags: [],
    sticker: null
  });
  
  // Filter states - default to show all stickers
  const [filters, setFilters] = useState({
    category: '', // Empty means all categories
    tag: '', // Empty means all tags
    limit: 10,
    offset: 0
  });
  
  const [totalPages, setTotalPages] = useState(0);
  const [newTag, setNewTag] = useState('');

  // Categories and types
  const [categories, setCategories] = useState(['sports', 'emotions', 'celebration', 'team', 'general']);
  const types = ['image', 'gif', 'animation'];
  const [newCategory, setNewCategory] = useState('');

  useEffect(() => {
    fetchStickers();
  }, [filters]);

  const fetchStickers = async () => {
    setLoading(true);
    try {
      const response = await getStickers(filters);
      if (response.statusCode === "10000") {
        setStickers(response.data.stickers || []);
        setTotalPages(Math.ceil((response.total || 0) / filters.limit));
      } else if (response.statusCode === "10003") {
        showSnackbar('Session expired. Please login again.', 'error');
        setTimeout(() => {
          window.location = '/login';
        }, 2000);
      } else {
        showSnackbar(response.message || 'Error fetching stickers', 'error');
        setStickers([]);
      }
    } catch (error) {
      showSnackbar('Error fetching stickers', 'error');
      setStickers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (sticker = null) => {
    if (sticker) {
      setEditingSticker(sticker);
      setFormData({
        title: sticker.title || '',
        type: sticker.type || 'image',
        category: sticker.category || '',
        tags: sticker.tags || [],
        sticker: null
      });
    } else {
      setEditingSticker(null);
      setFormData({
        title: '',
        type: 'image',
        category: '',
        tags: [],
        sticker: null
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSticker(null);
    setFormData({
      title: '',
      type: 'image',
      category: '',
      tags: [],
      sticker: null
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({
      ...prev,
      sticker: e.target.files[0]
    }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleAddCategory = () => {
    if (newCategory.trim() && !categories.includes(newCategory.trim())) {
      const updatedCategories = [...categories, newCategory.trim()];
      setCategories(updatedCategories);
      setFormData(prev => ({
        ...prev,
        category: newCategory.trim()
      }));
      setNewCategory('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      let response;
      if (editingSticker) {
        response = await updateSticker(editingSticker.id, formData);
      } else {
        response = await createSticker(formData);
      }
      
      if (response.statusCode === "10000") {
        showSnackbar(editingSticker ? 'Sticker updated successfully' : 'Sticker created successfully', 'success');
        handleCloseDialog();
        fetchStickers();
      } else if (response.statusCode === "10003") {
        showSnackbar('Session expired. Please login again.', 'error');
        setTimeout(() => {
          window.location = '/login';
        }, 2000);
      } else {
        showSnackbar(response.message || 'Error saving sticker', 'error');
      }
    } catch (error) {
      showSnackbar('Error saving sticker', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (stickerId) => {
    if (window.confirm('Are you sure you want to delete this sticker?')) {
      setLoading(true);
      try {
        const response = await deleteSticker(stickerId);
        if (response.statusCode === "10000") {
          showSnackbar('Sticker deleted successfully', 'success');
          fetchStickers();
        } else if (response.statusCode === "10003") {
          showSnackbar('Session expired. Please login again.', 'error');
          setTimeout(() => {
            window.location = '/login';
          }, 2000);
        } else {
          showSnackbar(response.message || 'Error deleting sticker', 'error');
        }
      } catch (error) {
        showSnackbar('Error deleting sticker', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      offset: 0 // Reset to first page when filter changes
    }));
  };

  const handlePageChange = (event, page) => {
    setFilters(prev => ({
      ...prev,
      offset: (page - 1) * prev.limit
    }));
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <div>
      <Component>
        <div className="page-body">
          <div className="container-fluid">
            <div className="page-header">
              <div className="row">
                <div className="col-sm-6">
                  <h3>Stickers Management</h3>
                </div>
                <div className="col-sm-6">
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleOpenDialog()}
                    sx={{ float: 'right' }}
                  >
                    Add New Sticker
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="container-fluid">
            <div className="row">
              <div className="col-sm-12">
                <div className="card">
                  <div className="card-body">
                    {/* Filters */}
                    <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <FormControl sx={{ minWidth: 120 }}>
                        <InputLabel>Category</InputLabel>
                        <Select
                          name="category"
                          value={filters.category}
                          onChange={handleFilterChange}
                          label="Category"
                        >
                          <MenuItem value="">All Categories (Default)</MenuItem>
                          {categories.map(cat => (
                            <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      
                      <TextField
                        name="tag"
                        label="Tag Filter"
                        value={filters.tag}
                        onChange={handleFilterChange}
                        placeholder="Filter by tag"
                        sx={{ minWidth: 150 }}
                      />
                      
                      <FormControl sx={{ minWidth: 100 }}>
                        <InputLabel>Limit</InputLabel>
                        <Select
                          name="limit"
                          value={filters.limit}
                          onChange={handleFilterChange}
                          label="Limit"
                        >
                          <MenuItem value={5}>5</MenuItem>
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={20}>20</MenuItem>
                          <MenuItem value={50}>50</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Stickers Grid */}
                    {loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <Grid container spacing={3}>
                        {stickers.map((sticker) => (
                          <Grid item xs={12} sm={6} md={4} lg={3} key={sticker.id}>
                            <Card sx={{ maxWidth: 300, margin: 'auto' }}>
                              <CardMedia
                                component="img"
                                height="200"
                                image={sticker.sticker_url || sticker.url}
                                alt={sticker.title}
                                sx={{ objectFit: 'contain' }}
                              />
                              <CardContent>
                                <Typography gutterBottom variant="h6" component="div">
                                  {sticker.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Category: {sticker.category}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Type: {sticker.type}
                                </Typography>
                                <Box sx={{ mt: 1 }}>
                                  {sticker.tags && sticker.tags.map((tag, index) => (
                                    <Chip
                                      key={index}
                                      label={tag}
                                      size="small"
                                      sx={{ mr: 0.5, mb: 0.5 }}
                                    />
                                  ))}
                                </Box>
                              </CardContent>
                              <CardActions>
                                <IconButton
                                  color="primary"
                                  onClick={() => handleOpenDialog(sticker)}
                                >
                                  <Edit />
                                </IconButton>
                                <IconButton
                                  color="error"
                                  onClick={() => handleDelete(sticker.id)}
                                >
                                  <Delete />
                                </IconButton>
                              </CardActions>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                        <Pagination
                          count={totalPages}
                          page={Math.floor(filters.offset / filters.limit) + 1}
                          onChange={handlePageChange}
                          color="primary"
                        />
                      </Box>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Add/Edit Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingSticker ? 'Edit Sticker' : 'Add New Sticker'}
          </DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="title"
                    label="Title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      label="Type"
                    >
                      {types.map(type => (
                        <MenuItem key={type} value={type}>{type}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      label="Category"
                      required
                    >
                      {categories.map(cat => (
                        <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      label="Add New Category"
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCategory())}
                      size="small"
                      sx={{ flexGrow: 1 }}
                    />
                    <Button onClick={handleAddCategory} variant="outlined">
                      Add Category
                    </Button>
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      label="Add Tag"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                      size="small"
                    />
                    <Button onClick={handleAddTag} variant="outlined">
                      Add
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {formData.tags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        onDelete={() => handleRemoveTag(tag)}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUpload />}
                    fullWidth
                    sx={{ py: 2 }}
                  >
                    {formData.sticker ? formData.sticker.name : 'Upload Sticker File'}
                    <input
                      type="file"
                      hidden
                      accept="image/*,.gif"
                      onChange={handleFileChange}
                      required={!editingSticker}
                    />
                  </Button>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={loading}>
                {loading ? <CircularProgress size={20} /> : (editingSticker ? 'Update' : 'Create')}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Component>
    </div>
  );
}

export default Stickers;
